import React from "react";
import { Flex } from "../flex";
import { InputLabel } from "../input-label";
import { InputError } from "../input-error";
import { Text } from "../text";
import { useSpace } from "../space/space";
import { cx } from "../../utils/dom";
import { BorderRadius, Color, InputAppearance } from "../../utils/theme";
import { useTheme } from "../theme";
import { StyleProps } from "../../utils/types";

type FocusableElement = Element | HTMLOrSVGElement;

export type FieldProps<S extends string> = InputProps &
  StyleProps<S | "wrapper" | "label" | "input" | "description"> & {
    labelProps?:
      | React.LabelHTMLAttributes<HTMLLabelElement>
      | React.DOMAttributes<FocusableElement>;
    descriptionProps?: React.DOMAttributes<FocusableElement>;
    errorMessageProps?: React.DOMAttributes<FocusableElement>;
    children?: React.ReactNode;
    replaceDefaultControlWrapper?: boolean;
  };

export type FieldComponentProps<S extends string> = Omit<
  FieldProps<S>,
  "labelProps" | "descriptionProps" | "errorMessageProps"
>;

export type InputProps = {
  description?: React.ReactNode;
  label?: React.ReactNode;
  error?: React.ReactNode;
  append?: React.ReactNode;
  prepend?: React.ReactNode;
  validationStatus?: "error" | "success" | "warning";
  shape?: "rounded" | "sharp";
  inputRef?: React.RefObject<HTMLInputElement>;
  wrapperRef?: React.RefObject<HTMLDivElement>;
  appearance?: InputAppearance;
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  color?: Color;
  radius?: BorderRadius;
  isDisabled?: boolean;
};

export function Field<S extends string = any>({
  label,
  description,
  error,
  color = "gray",
  radius,
  size = "md",
  className,
  classNames,
  style,
  styles,
  prepend,
  append,
  appearance = "outline",
  validationStatus,
  labelProps,
  errorMessageProps,
  descriptionProps,
  children,
  replaceDefaultControlWrapper,
  isDisabled,
  wrapperRef,
  ...props
}: FieldProps<S>) {
  const space = useSpace();
  const theme = useTheme();

  return (
    <Flex
      ref={wrapperRef}
      {...props}
      data-color={color}
      data-radius={radius ?? theme?.radius ?? "lg"}
      data-size={size}
      className={cx(
        "p-input-group",
        space.compactItemWrapperAdjust,
        className,
        classNames?.wrapper
      )}
      stack
      gap={"3xs"}
      style={{ ...style, ...styles?.wrapper }}
    >
      {label && (
        <InputLabel
          style={styles?.label}
          className={classNames?.label}
          color={color}
          {...labelProps}
        >
          {label}
        </InputLabel>
      )}

      {!replaceDefaultControlWrapper && (
        <Flex
          align="center"
          className={cx(space.classNames, "p-input-control")}
          data-has-error={!!error}
          data-appearance={appearance}
          data-disabled={isDisabled}
          stretchX
          inline
          position="relative"
        >
          {prepend && <Flex className="add-on add-on-left">{prepend}</Flex>}
          {/**TODO: this is where actual input goes */}
          {children}
          {append && <Flex className="add-on add-on-right">{append}</Flex>}
        </Flex>
      )}

      {replaceDefaultControlWrapper && children}

      {error && (
        <InputError validationStatus={validationStatus} {...errorMessageProps}>
          {error}
        </InputError>
      )}

      {description && (
        <Text
          as="div"
          variant="light"
          color="gray"
          size="sm"
          lineHeight={"short"}
          {...descriptionProps}
        >
          {description}
        </Text>
      )}
    </Flex>
  );
}

Field.displayName = "Field";
